import { inject, injectable } from 'inversify';
import { catchError, retry, take, timeout } from 'rxjs/operators';
import {
    RESOLVE_PACED_GIVE_UP_COPY,
    RESOLVE_PACED_TAG,
    ResolvePacingState,
    isPacedResolveError,
    isTerminalResolveStatus,
} from '../utils/resolve-retry.utils';
import { scheduleCoordinator } from './scheduling/schedule-coordinator';
import { SCHEDULE_RETRY_DELAYS_MS, ScheduleFailure, createScheduleRetryPolicy } from './scheduling/schedule-retry-policy';

import { firstValueFrom, fromEvent, Observable, of, throwError, timer } from 'rxjs';
import { ConnectivityUtils } from '../utils/connectivity.utils';
import { FileUploadRequestMetadata } from '../models/file-upload-request-metadata';
import { GoogleDriveFile } from '../models/google-drive-file';
import { Logger } from '../models/logger';
import { GAContants } from '../types/ga.types';
import { OperationType, OperationTypes, ProcessorMetadata } from '../types/types';
import { BrowserUtils } from '../ui-utils/browser-utils';
import { getFileMimeType } from '../utils/file';
import { NetworkError, RequestUtils } from '../utils/request.utils';
import { UrlUtils } from '../utils/url.utils';
import { GAService } from './analytics.service';
import { ApiConfig } from './api-config';
import { ConnectivityService } from './connectivity.service';
import { EnforcementErrorService } from './enforcement-error.service';
import { EnvironmentService } from './environment.service';
import { LightHttp } from './light-http.service';
import { SettingsService } from './settings.service';
import { WebSocketService } from './websocket.service';


@injectable()
export class ProcessorResolverService {
    private readonly logger = Logger.getLogger('ProcessorResolverService');

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(GAService) private gaService: GAService,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(EnvironmentService) private env: EnvironmentService,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(ConnectivityService) private connectivityService: ConnectivityService,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(EnforcementErrorService) private enforcementErrorService: EnforcementErrorService,
    ) {
    }

    private getDomainToBeUsed() {
        try {
            let useDomain = null;
            let useDomainValidator = null;
            if (BrowserUtils.hasWindow()) {
                useDomain = UrlUtils.getParameterByName(window.location.href, 'useDomain') || window['useDomain'] || null;
                useDomainValidator = UrlUtils.getParameterByName(window.location.href, 'useDomainValidator') || window['useDomainValidator'] || null;
            }
            if (!useDomain) {
                return {};
            }
            return {
                useDomain,
                useDomainValidator,
            };
        } catch (e) {
            return {};
        }
    }

    async generateFileUploadRequestMetadata(
        file: File | GoogleDriveFile,
        fromType: string[],
        toType: string,
        operationType: OperationType,
        resolveOptions?: {
            suppressGA?: boolean,
            excludeDomains?: string[],
            /** gif-maker (wave 2 ERRORS): the caller renders the refusal itself — do not open the global "You've hit a limit" modal. Default: unchanged. */
            suppressEnforcementModal?: boolean,
            /** Code 1: told while a plain 429 paces this resolve (the row shows "Queued — server busy, retrying"); null when it ends. */
            onPaced?: (state: ResolvePacingState | null) => void,
        },
    ): Promise<FileUploadRequestMetadata> {
        await this.wsService.ensureWsDomain();
        if (!this.wsService.getDomain()) {
            this.logger.warn({ message: 'upload.resolve.wsDomainEmpty', data: { wsId: this.wsService.getId() } });
        }
        await firstValueFrom(this.settingsService.userSettings.pipe(take(1), timeout(5000), catchError(() => of(null))));

        let test = 'false';
        if (BrowserUtils.hasWindow()) {
            test = UrlUtils.getParameterByName(window.location.href, 'test') || 'false';
        }
        const { useDomain, useDomainValidator } = this.getDomainToBeUsed();

        if (!resolveOptions?.suppressGA) {
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-START'],
                GAContants.EventLabel.CONVERT,
                `${file.name}-${file.size}`,
            );
        }
        try {
            const options = {
                params: {
                    from: fromType,
                    to: toType,
                    operation: operationType,
                    contentNames: file.name,
                    contentTypes: getFileMimeType(file),
                    contentSizes: file.size + '',
                    test,
                    wsId: this.wsService.getId(),
                    wsDomain: this.wsService.getDomain(),
                    requireActiveDownload: (file instanceof File || file instanceof Blob) ? 'false' : 'true',
                    ...(useDomain ? { useDomain, useDomainValidator } : null),
                    // upload-skip-edge (QA fu-r3 T9): the failed edges are matched by host inside `getEdgesRandomized`. The old
                    // `.filter(d => !excludeDomains.includes(d))` compared `https://ncf-…` with bare `d` and never skipped one.
                    ...(operationType === OperationTypes.UPLOAD ? { preferedDomains: this.connectivityService.getEdgesRandomized(4, resolveOptions?.excludeDomains).map(edge => edge.domain) } : {}),
                },
            };

            // this.http.get<ProcessorMetadata[]>(`${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`, options)
            //         .pipe(
            //             retry(10),
            //         ).subscribe({
            //             error: (e) => {
            //                 console.log(e)
            //             }
            //         })

            const request$ = () => RequestUtils.getSuccessValueOrThrow$<ProcessorMetadata[]>(
                [
                    this.http.get<ProcessorMetadata[]>(`${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`, options)

                ],
                8000
            ).pipe(
                retry({
                    count: 5,
                    delay: (error, retryCount) => {
                        // 2026-09-07: a verdict-type 4xx (406 "file type is not supported",
                        // 400, 413, 415 …) does not change on retry; the ladder used to spend
                        // ~31 s re-asking before showing the same answer. 408 still retries here.
                        if (isTerminalResolveStatus((error as { status?: number })?.status)) {
                            return throwError(() => error);
                        }
                        // Code 1: a plain 429 (+ Retry-After, no enforcement envelope) is PACING. Not this
                        // blind 1/2/4/8/16 s ladder: `resolvePaced` below waits the server's Retry-After on
                        // the batch-wide gate, so N dropped files do not each re-ask five times.
                        if (isPacedResolveError(error)) {
                            return throwError(() => error);
                        }
                        // Enforcement / billing rejections (HTTP 429 enforcement_error,
                        // HTTP 402 insufficient_credits) MUST short-circuit the retry
                        // loop. Pre-Phase-7 we retried 5× with exponential backoff,
                        // which hid the rejection from the user — Convert button hung
                        // in "Loading…" forever (runbook bug 6a-B1, P0).
                        if (EnforcementErrorService.isEnforcementError(error)) {
                            if (!resolveOptions?.suppressEnforcementModal) {
                                this.enforcementErrorService.show(
                                    EnforcementErrorService.extractDetails(error),
                                );
                            }
                            return throwError(() => error);
                        }
                        if (typeof navigator !== 'undefined' && !navigator.onLine) {
                            return fromEvent(window, 'online').pipe(
                                take(1),
                                timeout(30000),
                            );
                        }
                        return timer(Math.min(1000 * Math.pow(2, retryCount - 1), 16000));
                    },
                }),
            );
            const data: ProcessorMetadata[] = await this.resolvePaced(request$, resolveOptions?.onPaced);
            if (!resolveOptions?.suppressGA) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['NETWORK'],
                    GAContants.EventAction['FILE-PROCESSOR-RESOLVE-END'],
                    GAContants.EventLabel.CONVERT,
                    `${file.name}-${file.size}`,
                );
            }
            return new FileUploadRequestMetadata(data, file);
        } catch (e) {
            if (!resolveOptions?.suppressGA) {
                this.gaService.send(
                    GAContants.HitType.EVENT,
                    GAContants.EventCategory['NETWORK'],
                    GAContants.EventAction['FILE-PROCESSOR-RESOLVE-FAILED'],
                    GAContants.EventLabel.CONVERT,
                    `${file.name}-${file.size}`,
                );
            }
            console.error(e);
            // Preserve enforcement-rejection context so the caller (and any
            // outer retry layer) can recognise it via
            // EnforcementErrorService.isEnforcementError(...) and bail
            // instead of wrapping it as a generic resolve failure.
            if (EnforcementErrorService.isEnforcementError(e)) {
                throw e;
            }
            // Code 1: the pacing budget is spent — a friendly NetworkError, tagged so the caller does
            // not blind-retry the whole 5 minutes again (`isResolvePacedGiveUp`).
            if (e && typeof e === 'object' && (e as Record<string, unknown>)[RESOLVE_PACED_TAG] === true) {
                const paced = new NetworkError(RESOLVE_PACED_GIVE_UP_COPY);
                (paced as unknown as Record<string, unknown>)[RESOLVE_PACED_TAG] = true;
                (paced as unknown as { status?: number }).status = 429;
                throw paced;
            }
            if (!ConnectivityUtils.isOnline()) {
                throw new NetworkError('You appear to be offline. Please check your connection and try again.');
            }
            const errorSuffix = ' Error was thrown while trying to resolve edge servers.';
            if (e instanceof NetworkError) {
                throw new NetworkError(e.message + errorSuffix);
            } else {
                throw new Error(e.message + errorSuffix);
            }
        }
    }

    /**
     * Code 1 — pacing on `/servers/resolve` (ENFORCEMENT-POLICY.md §6.2 item 1, ruling R6: a client
     * behaviour). professorx answers a plain `HTTP 429` + `Retry-After` + `{code:'rate_paced',
     * retryAfterSeconds}` BEFORE the upload starts. The same `ScheduleRetryPolicy` as the schedule
     * ladder decides (Retry-After honoured, else jittered 2/5/10/20/30 s, 5-minute budget), and the
     * Retry-After is held on the shared `BatchScheduleGate` so every card of the batch waits for the
     * same instant. Retry attempts also park while the tab is hidden (code 10 (b)).
     *
     * A 429 WITH the enforcement envelope never reaches here: the inner ladder throws it and the
     * caller's enforcement branch renders the denial (modal once per batch, card line) — the file is
     * never uploaded. When the budget is spent the error is tagged `resolvePaced` and the row fails
     * with its Retry.
     */
    private async resolvePaced<T>(
        request$: () => Observable<T>,
        onPaced?: (state: ResolvePacingState | null) => void,
    ): Promise<T> {
        const gate = scheduleCoordinator.gate;
        const policy = createScheduleRetryPolicy({ startedAtMs: Date.now() });
        let attempt = 0;
        let paced = false;
        for (; ;) {
            attempt++;
            await gate.whenUnpaused({ honourHidden: attempt > 1 });
            try {
                const value = await firstValueFrom(request$());
                if (paced) onPaced?.(null);
                return value;
            } catch (e: any) {
                if (!isPacedResolveError(e)) {
                    if (paced) onPaced?.(null);
                    throw e;
                }
                const failure: ScheduleFailure = {
                    status: e?.status,
                    response: e?.response,
                    // `retryAfterSeconds` is set by the HTTP layer from the header or the body (utils/retry-after.ts).
                    retryAfter: typeof e?.retryAfterSeconds === 'number'
                        ? e.retryAfterSeconds
                        : (typeof e?.response?.retryAfterSeconds === 'number' ? e.response.retryAfterSeconds : null),
                };
                const decision = policy.decide(failure, attempt);
                if (decision.kind !== 'retry') {
                    if (paced) onPaced?.(null);
                    (e as Record<string, unknown>)[RESOLVE_PACED_TAG] = true;
                    this.logger.warn({ message: `Resolve pacing budget spent after ${attempt} attempts (${decision.reason}).`, error: e });
                    throw e;
                }
                const delayMs = decision.delayMs ?? 0;
                // The server's Retry-After pauses the WHOLE batch (schedule calls included); a ladder
                // wait is this card's own.
                if (decision.fromRetryAfter) gate.pauseFor(delayMs);
                paced = true;
                onPaced?.({ attempt: attempt + 1, maxAttempts: SCHEDULE_RETRY_DELAYS_MS.length, delayMs, fromRetryAfter: decision.fromRetryAfter });
                this.logger.trace7({ message: `Resolve paced by the server: attempt ${attempt + 1} in ${delayMs} ms`, data: { fromRetryAfter: decision.fromRetryAfter } });
                await new Promise<void>(resolve => setTimeout(resolve, Math.max(0, delayMs)));
            }
        }
    }

    async generateFileUploadRequestMetadataForGenericDataProcessing(): Promise<FileUploadRequestMetadata> {
        let test = 'false';
        if (BrowserUtils.hasWindow()) {
            test = UrlUtils.getParameterByName(window.location.href, 'test') || 'false';
        }
        this.gaService.send(
            GAContants.HitType.EVENT,
            GAContants.EventCategory['NETWORK'],
            GAContants.EventAction['FILE-PROCESSOR-RESOLVE-START'],
            GAContants.EventLabel.CONVERT,
            `generic-generic`,
        );
        try {
            const options = {
                params: {
                    from: 'generic',
                    to: 'generic',
                    operation: OperationTypes.PROCESS,
                    contentNames: 'generic.txt',
                    contentTypes: 'text/plain',
                    contentSizes: '0',
                    test,
                    wsId: this.wsService.getId(),
                    wsDomain: this.wsService.getDomain(),
                    requireActiveDownload: 'false',
                },
            };

            const data: ProcessorMetadata[] = await RequestUtils.getSuccessValueOrThrow<ProcessorMetadata[]>(
                this.http.get<ProcessorMetadata[]>(
                    `${this.apiConfig.getUploadAndProcessEndpointResolveEndpoint()}`,
                    options
                ),
            );

            this.logger.trace1({
                message: 'ProcessMetadata',
                data: JSON.stringify(data),
            });

            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-END'],
                GAContants.EventLabel.CONVERT,
                `generic-generic`,
            );
            return new FileUploadRequestMetadata(data, null);
        } catch (e) {
            this.logger.error({
                message: e.message || 'Error getting process metadata',
                error: e,
            });
            this.gaService.send(
                GAContants.HitType.EVENT,
                GAContants.EventCategory['NETWORK'],
                GAContants.EventAction['FILE-PROCESSOR-RESOLVE-FAILED'],
                GAContants.EventLabel.CONVERT,
                `generic-generic`,
            );
        }
    }
}
